/**
 * 全量上报点击事件
 *
 * 提供方法
 * registerEvent: 注册事件，目前只有click
 * 参数：
 * el: 主要监听的节点，不传默认为document，可以结合router守卫监听指定的页面
 * useCapture: 事件句柄在捕获、冒泡阶段执行
 */
import { biHottag } from './mta';
import { CustomException } from '../error';

let timer; // 上报定时器，用于取消定时器

function isNode(el) {
  return el && el.nodeType === Node.ELEMENT_NODE;
}

function isDocument(el) {
  return el && el.nodeType === Node.DOCUMENT_NODE;
}

function isTag(el, tag) {
  return el && el.tagName && (el.tagName.toLowerCase() === tag.toLowerCase());
}

function getTarget(e) {
  if (typeof e.target === 'undefined') {
    return e.srcElement;
  }

  return e.target || {};
}

function getClassName(el) {
  let { className } = el;
  const { tagName, nodeType } = el;

  if (typeof className === 'object' && className.baseVal) {
    className = className.baseVal;
  }

  if (className && typeof className === 'string') {
    // split切割包含多个空格, 解决部分浏览器不支持trim的问题
    let str;
    try {
      str = `.${className.trim().split(/\s+/)
        .join('.')}`; // trim过滤classname为 " vs-toggle" 的情况
    } catch {
      throw new CustomException(err, 'FullReportClassNameFormatFail', 'in base-libs/full-report.js, 全量上报class格式化失败');
    }

    if (+str === 0) {
      new CustomException({
        tagName,
        nodeType,
        className: JSON.stringify(el.className),
      }, 'FullReportClassNameFormatReport', '全量上报class格式化失败');
      return '';
    }

    return str;
  }

  return '';
}

function getTagName(el) {
  return (el.tagName && el.tagName.toLowerCase()) || '';
}

function reverse(arr) {
  // arr.pop是一个方法，map第二个参数作为作用域传入，也就是pop处理第二个参数，返回最后一个值到第一个值
  return [...arr].map(arr.pop, arr);

  // 等同于
  // return [...arr].map((item) => {
  //   return arr.pop();
  // });
}

/**
 * 获取点击对象的xPath路径。解释路径组成: 标签div名称 + 标签index序号 + 标签的class名称（如果有多个将以.拼接）
 * 例如： body.0|div.2|div.0.page-repay|div.0.box|div.0.fit-navbar|div.1.fit-navbar-item|span.0
 * @param e 事件对象
 */
function getXpath(e) {
  let path = [];

  if (e.path && e.path.length > 0 && isNode(e.path[0])) {
    path = e.path; // 某些安卓机型上，不是一个数组
    console.log('full-report:getXpath', typeof path);
  }

  // path路径不存在 或者 path不是一个数组 或者 path的长度为0，使用冒泡方式获取节点数组
  if ((!path || !Array.isArray(path) || path.length === 0) && e.target) {
    let target = getTarget(e);
    path = [];

    while (target.parentNode && !isTag(target, 'html')) {
      path.push(target);
      target = target.parentNode;
    }
  }

  if (path.length > 0) {
    return reverse(path)
      // 原生reverse有浏览器兼容, 并且ios12有bug https://www.sohu.com/a/254803083_609397?qq-pf-to=pcqq.group
      .filter(el => isNode(el) && !isTag(el, 'html')) // 过滤掉非node点与html的节点，e.path会包含html节点，html节点不需要统计，所以可以去除掉
      .map((el) => {
        try {
          // body.0
          if (el && el.parentNode) {
            const tagName = getTagName(el);
            const className = getClassName(el);

            const parentTagName = getTagName(el.parentNode);
            const parentClassName = getClassName(el.parentNode);

            const selector = `${tagName}${className}`;
            // ul.box > li.item
            const selectors = el.parentNode.querySelectorAll(`${parentTagName}${parentClassName} > ${selector}`);

            const index = Array.prototype.indexOf.call(selectors, el);

            return `${tagName}.${index}${className}`;
          }
        } catch (e) {
          new CustomException(e, 'FullReportMapListError', '全量上报处理数据失败');
          return '';
        }
      })
      .join('|');
  }

  return '';
}

/**
 * 精简函数
 * @param arr 数组
 * @param i 下标
 * @param len 最大长度
 */
function short(arr, i, len) {
  let item = arr[i];
  const sub = item.split('.');
  item = sub.slice(0, sub.length > 2 ? 3 : 2);
  arr[i] = item.join('.');
  const str = arr.join('|');

  // 如果循环到最后一个，就从尾部往前去len位
  if (i >= arr.length - 1) {
    return str.slice(-len);
  }
  if (str.length < len) {
    return str;
  }
  return short(arr, i + 1, len);
}

/**
 * 全埋点上报 - 精简 xPath 长度
 * TODO: getShortPath 借鉴于理财通stat.js
 * @param xpath
 * @param len
 */
function getShortPath(xPath, len) {
  if (xPath.length < len) {
    return xPath;
  }
  const arr = xPath.split('|');
  return short(arr, 0, len);
}

// 全埋点 - 从vue中获取绑定事件的dom (copy from stat.js)
function fundBindDomsOfVue() {
  const tempEle = [];
  const maxTime = 50;
  const abnormalTime = 10;
  let componentCh = [];
  const startTime = new Date().getTime();
  const app = window.VueRoot; // 需要对应业务模块把new Vue赋给window.Vue
  if (!app) {
    return [];
  }
  f(app);
  if (componentCh.length) {
    componentCh.forEach(w => fc(w));
  }

  const endTime = new Date().getTime();
  if ((endTime - startTime) > abnormalTime) {
    new CustomException({
      time: endTime - startTime,
    }, 'fundBindDomsOfVue', '获取事件列表超过10ms');
  }


  if ((endTime - startTime) > maxTime) {
    // @ts-ignore
    fundBindDomsOfVue = () => [];
    new CustomException({
      time: endTime - startTime,
    }, 'fundBindDomsOfVue', '获取事件列表超过50ms');
  }

  return tempEle;

  // components的处理
  function f(v) {
    const vn = v._vnode || {}; // 当前组件的虚拟节点
    const ch = v.$children; // 获取组件子节点，vueComponent对象数组

    if (vn.data && vn.data.on && vn.data.on.click) {
      tempEle.push(vn.elm);
    }

    // children，dom节点集合，不包含vueComponent
    if (vn.children && vn.children.length) {
      componentCh = componentCh.concat(vn.children);
    }

    if (ch && ch.length) {
      ch.forEach(w => f(w));
    }
  }

  // components里的分支处理
  function fc(v) {
    const ch = v.children;

    if (v.data && v.data.on && v.data.on.click) {
      tempEle.push(v.elm);
    }

    if (ch && ch.length) {
      ch.forEach(w => fc(w));
    }
  }
}

/**
 * dom节点是否添加点击事件
 * @param dom 点击的点
 * @param aim 匹配的事件节点
 */
function isBind(dom, aim) {
  if (dom && dom.tagName && !['html', 'body'].includes(dom.nodeName.toLowerCase())) {
    if (dom === aim) {
      return aim;
    }
    return isBind(dom.parentNode, aim); // 点击事件有可能绑定在父节点
  }
  return false;
}

/**
 * 去重
 */
function deWeight(arr) {
  const list = arr || [];
  const doSet = new Set(list);
  return [...doSet];
}

function checkEventBind(dom) {
  if (!dom) {
    return false;
  }

  // 过滤html，body
  if (dom.nodeName && ['html', 'body'].includes(dom.nodeName.toLowerCase())) {
    return false;
  }

  const eventDomList = fundBindDomsOfVue();
  const targetEventDomList = deWeight(eventDomList);
  const len = targetEventDomList.length;

  for (let i = 0; i < len; i++) {
    if (isBind(dom, targetEventDomList[i])) {
      return true;
    }
  }

  return false;
}

/**
 * 点击回调事件
 * @param e 事件对象
 */
function clickEvent(e) {
  timer = setTimeout(() => {
    // 获取xPath
    const xPath = getXpath(e);
    const isbindClick = checkEventBind(getTarget(e));
    const len = xPath.length;

    // 存在xPath，上报bi
    if (len > 0) {
      const extraParams = {
        // getShortPath 借鉴于理财通stat.js
        fxpath: getShortPath(xPath, 1024),
        fxpath_len: len, // 跟short之后不一样，可以判断出精简过
        fis_event_response: isbindClick ? 1 : 0, // 比如说，有一个按钮，按了之后，会有下一步的动作，但没有上报，这个值就标志为1，算预埋的点
        fis_embed_event: 0, // 是否有预埋点（预埋点手动上报，所以这里默认为不是预埋点）
      };
      console.log('fullReportParams', extraParams);
      biHottag('develop.check.lose_stat', extraParams);
    }
  }, 100);
}

/**
 * 清除上报
 */
function clearFullReport() {
  clearTimeout(timer);
}

/**
 * 移除事件，目前只支持click
 * @param el dom节点
 * @param useCapture 句柄在冒泡还是捕捉阶段执行，默认为true
 */
async function removeEvent(el, useCapture) {
  const support = isNode(el) || isDocument(el);
  if (support) {
    el.removeEventListener('click', clickEvent, useCapture);
  }
}

/**
 * 注册事件，目前只支持click
 * @param el 监听节点
 * @param useCapture 句柄在冒泡还是捕捉阶段执行，默认为true
 */
async function registerEvent(el = document, useCapture = true) {
  const support = isNode(el) || isDocument(el);
  if (support) {
    el.addEventListener('click', clickEvent, useCapture);
  }
}

async function removeFullReport(el = document, useCapture = true) {
  try {
    removeEvent(el, useCapture);
  } catch (e) {
    new CustomException(e, 'FullReportRemoveError', '全量上报移除事件失败');
  }
}

async function initFullReport(isFull = false, el = document, useCapture = true) {
  // 是否需要监控上报
  if (!isFull) {
    return;
  }

  try {
    registerEvent(el, useCapture);
  } catch (e) {
    new CustomException(e, 'FullReportInitError', '全量上报初始化事件失败');
  }
}

export {
  clearFullReport,
  removeFullReport,
  initFullReport,
};
